import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import css from '@/components/common/Common.module.css'

interface Stats {
  quotesCount: Record<string, number>
  ordersCount: Record<string, number>
  recentActivity: Array<{ id: string; summary: string; activity_type: string; created_at: string }>
  activeProjects: number
}

export function DashboardPage() {
  const { profile } = useAuth()
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!profile?.organization_id) {
      setLoading(false)
      return
    }

    async function loadStats() {
      const orgId = profile!.organization_id!
      const [quotesRes, ordersRes, activityRes, projectsRes] = await Promise.all([
        supabase.from('quotes').select('status').eq('organization_id', orgId),
        supabase.from('orders').select('status').eq('organization_id', orgId),
        supabase.from('activity_log').select('id, summary, activity_type, created_at')
          .eq('organization_id', orgId).order('created_at', { ascending: false }).limit(10),
        supabase.from('projects').select('id', { count: 'exact', head: true })
          .eq('organization_id', orgId).in('status', ['draft', 'active']),
      ])

      const quotesCount: Record<string, number> = {}
      quotesRes.data?.forEach(q => { quotesCount[q.status] = (quotesCount[q.status] || 0) + 1 })

      const ordersCount: Record<string, number> = {}
      ordersRes.data?.forEach(o => { ordersCount[o.status] = (ordersCount[o.status] || 0) + 1 })

      setStats({
        quotesCount,
        ordersCount,
        recentActivity: activityRes.data || [],
        activeProjects: projectsRes.count || 0,
      })
      setLoading(false)
    }

    loadStats()
  }, [profile])

  if (loading) return <div style={{ color: 'var(--color-text-muted)', padding: 'var(--space-8)' }}>Loading dashboard...</div>

  if (!profile?.organization_id) {
    return (
      <div>
        <PageHeader title="Welcome to Revenue Workspace" description="Your account hasn't been assigned to an organization yet. Contact your administrator." />
      </div>
    )
  }

  const totalQuotes = Object.values(stats?.quotesCount || {}).reduce((a, b) => a + b, 0)
  const totalOrders = Object.values(stats?.ordersCount || {}).reduce((a, b) => a + b, 0)
  const draftQuotes = stats?.quotesCount['draft'] || 0
  const activeOrders = totalOrders - (stats?.ordersCount['delivered'] || 0) - (stats?.ordersCount['cancelled'] || 0)

  return (
    <div>
      <PageHeader title={`Good ${getTimeOfDay()}, ${profile.full_name?.split(' ')[0] || 'there'}`} description="Here's what's happening in your workspace" />

      <div className={css.grid4} style={{ marginBottom: 'var(--space-8)' }}>
        <div className={css.widget}>
          <div className={css.widgetHeader}><span className={css.widgetTitle}>Active Projects</span></div>
          <div className={css.widgetValue}>{stats?.activeProjects || 0}</div>
        </div>
        <div className={css.widget}>
          <div className={css.widgetHeader}><span className={css.widgetTitle}>Total Quotes</span></div>
          <div className={css.widgetValue}>{totalQuotes}</div>
          {draftQuotes > 0 && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-technical)', marginTop: 'var(--space-1)' }}>{draftQuotes} draft</div>}
        </div>
        <div className={css.widget}>
          <div className={css.widgetHeader}><span className={css.widgetTitle}>Active Orders</span></div>
          <div className={css.widgetValue}>{activeOrders}</div>
        </div>
        <div className={css.widget}>
          <div className={css.widgetHeader}><span className={css.widgetTitle}>Total Orders</span></div>
          <div className={css.widgetValue}>{totalOrders}</div>
        </div>
      </div>

      <div className={css.grid2}>
        <div className={css.widget}>
          <div className={css.widgetHeader}>
            <span className={css.widgetTitle}>Quick Actions</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <Link to="/gaviota/projects/new" className={css.btnSecondary}>New Project</Link>
            <Link to="/gaviota/quotes/new" className={css.btnSecondary}>New Quote</Link>
            <Link to="/gaviota/catalog" className={css.btnSecondary}>Browse Catalog</Link>
          </div>
        </div>

        <div className={css.widget}>
          <div className={css.widgetHeader}>
            <span className={css.widgetTitle}>Recent Activity</span>
          </div>
          {stats?.recentActivity.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>No recent activity</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {stats?.recentActivity.map(item => (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span style={{ fontSize: 'var(--text-sm)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.summary}</span>
                  <StatusBadge label={item.activity_type.replace(/_/g, ' ')} variant="default" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function getTimeOfDay() {
  const h = new Date().getHours()
  if (h < 12) return 'morning'
  if (h < 18) return 'afternoon'
  return 'evening'
}
